import type { GptSourceRef } from "./transcript";

/**
 * How a cited source states its identity, everywhere JuisciGPT cites one.
 *
 * "Kopetz S et al. · 2025" — the first author and the year. A clinician indexes
 * the literature by first author, year and journal, not by title, so this line
 * is what makes a citation findable again. The journal is rendered beside it by
 * each surface (it comes from a different field per provenance).
 *
 * Owned here rather than by either engine's components: the General sources
 * list, the Chat-with-Paper card and the four Evidence surfaces all cite the
 * same way, and a citation that reads differently depending on which engine
 * answered is the bug this replaced.
 *
 * Returns "" when the source carries neither an author nor a year — a bare web
 * result. The caller decides what to render instead; nothing here invents a
 * substitute.
 */
export function citationIdentity(source: {
  // `undefined` is a WIRE reality, not sloppiness: a bundle can be one contract
  // version ahead of the API it talks to (a rolling deploy's split traffic, dev
  // HMR against an older api process, the preview proxy to prod), and on that
  // older wire these fields do not exist yet. Absent means what a null means —
  // identity unknown — so it degrades to the same empty line instead of
  // crashing the answer.
  authors: readonly string[] | undefined;
  year: number | null | undefined;
}): string {
  const authors = source.authors === undefined ? [] : source.authors;
  const first = authors.at(0);
  const authorPart =
    first === undefined ? null : authors.length > 1 ? `${first} et al.` : first;
  return citationIdentityOf(
    authorPart === null ? "" : authorPart,
    source.year === undefined ? null : source.year
  );
}

/**
 * The same line, from an author string a surface already formatted its own way.
 * An internal article states its first author through `authorLine` (it holds a
 * `primary_author` no snapshot has), so it cannot go through `citationIdentity`
 * above — but it must still join to the year identically, which is the whole
 * point of one owner.
 */
export function citationIdentityOf(
  author: string,
  year: number | null
): string {
  return [author === "" ? null : author, year === null ? null : String(year)]
    .filter((part) => part !== null)
    .join(" · ");
}

// ── Citation numbering ──────────────────────────────────────────────────────

// The canonical URLs the model cites a source by. Mirrors the api's
// `citedSourceKeys` (apps/api/src/domain/gpt-cited-sources.ts), which decides
// from the SAME shapes which sources a finished answer actually cited — the two
// have to agree, or a source would be listed with a number nothing points at.
// No `g` flag: `exec` on a global regex carries `lastIndex` between calls.
const PUBMED_CITE = /pubmed\.ncbi\.nlm\.nih\.gov\/(\d+)/;
// A slug may hold `:` and `.` inside, never at its end, so trailing punctuation stays out.
const JUISCI_CITE =
  /juisci\.com\/(?:post|article)\/([A-Za-z0-9_-](?:[A-Za-z0-9_.:-]*[A-Za-z0-9_-])?)/;

// Mirrors `normalizeUrl` in the api's `gpt-cited-sources.ts`, and must stay
// identical to it: that one decides which sources the turn LISTS, this one which
// number a citation resolves to, so a URL the two normalize differently is a
// listed source no chip can point at. A trailing slash is not semantically
// meaningful (the model occasionally drops it), and neither is case — a DOI is
// case-insensitive by spec, and the model re-canonicalises the lowercased DOI
// retrieval handed it.
function normalizeUrl(url: string): string {
  const lower = url.toLowerCase();
  return lower.endsWith("/") ? lower.slice(0, -1) : lower;
}

/**
 * Which listed source a citation link points at. The number is what the chip
 * shows; the source is what tapping it opens. Null when the answer cited
 * something the turn's source list does not hold.
 */
export type CitationHit = { number: number; source: GptSourceRef };

/**
 * The sources a Chat-with-Paper answer numbers its citations over: every listed
 * source but the paper itself. The paper is what the whole conversation is
 * about, drawn as its own card above the numbered list, never as a citation
 * among the others. The Sources tab and the inline chips both number from this
 * list, so a chip's number is always a row the tab shows.
 */
export function citedBesidesPaper(
  sources: readonly GptSourceRef[],
  paperArticleId: string
): GptSourceRef[] {
  return sources.filter(
    (source) =>
      source.provenance !== "juisci_internal" || source.id !== paperArticleId
  );
}

/**
 * Number a turn's sources, and resolve an inline citation's link to its number.
 *
 * The number IS the position in `sources` — the same array the Sources tab
 * lists — so the chip in the prose and the row in the tab cannot drift apart.
 * An uploaded document is never a link target (it has no URL), so it is
 * numbered in the list but resolves from no href.
 */
export function citationNumbering(
  sources: readonly GptSourceRef[]
): (href: string) => CitationHit | null {
  const byPmid = new Map<string, CitationHit>();
  const byArticleKey = new Map<string, CitationHit>();
  const byUrl = new Map<string, CitationHit>();

  for (const [index, source] of sources.entries()) {
    const target = { number: index + 1, source };
    switch (source.provenance) {
      case "pubmed":
        byPmid.set(source.externalId, target);
        continue;
      case "juisci_internal":
        // The model cites by whichever form the source link carried — a slug
        // (`/post/<slug>`) or the raw id (`/article/<id>`) — so both are keys.
        byArticleKey.set(source.id, target);
        if (source.slug !== null) byArticleKey.set(source.slug, target);
        continue;
      case "web":
        byUrl.set(normalizeUrl(source.url), target);
        continue;
      case "uploaded_document":
        continue;
      default:
        throw new Error(source satisfies never);
    }
  }

  return (href) => {
    const pmid = PUBMED_CITE.exec(href);
    const pmidKey = pmid === null ? undefined : pmid[1];
    if (pmidKey !== undefined) {
      const found = byPmid.get(pmidKey);
      if (found !== undefined) return found;
    }
    const article = JUISCI_CITE.exec(href);
    const articleKey = article === null ? undefined : article[1];
    if (articleKey !== undefined) {
      const found = byArticleKey.get(articleKey);
      if (found !== undefined) return found;
    }
    const web = byUrl.get(normalizeUrl(href));
    return web === undefined ? null : web;
  };
}
